// since in some case default data will be impacted by other hooks, so use hook not until function.

import _ from 'lodash';
import { mergeWithDefaults } from '@src/emp-3.0/utils';
import {
  ECalculationType,
  EMetricTypes,
  EOccurrenceType,
  EPairType,
  EWithinUnitType,
} from '@src/emp-3.0/types';
import { useCallback } from 'react';

const metricBaseInfo = {
  name: undefined,
  metric_category: 3,
  description: undefined,
};

const defaultDurationMetricData = {
  ...metricBaseInfo,
  metrics_type: EMetricTypes.DURATION,
  definition: {
    first_event: undefined,
    second_event: undefined,
    without_event: undefined,
    distribution: {
      max_value: undefined,
      min_value: undefined,
    },
    propagative_dimensions: [],
    pairing_logic: EPairType.FIRST_FIRST,
    occurrence_type: EOccurrenceType.FIRST_PAIR,
    need_distribution: 0,
    is_higher_better: false,
    // not showing in UI
    calculation: 'average',
    complete_timeout: 6,
    complete_timeout_unit: EWithinUnitType.HOUR,
  },
};

const defaultConversionMetricData = {
  ...metricBaseInfo,
  metrics_type: EMetricTypes.CONVERSION,
  definition: {
    first_event: undefined,
    second_event: undefined,
    without_event: undefined,
    propagative_dimensions: [],
    calculation: ECalculationType.CONVERSION_RATE,
    occurrence_type: EOccurrenceType.MULTIPLE,
    within_unit: EWithinUnitType.SECOND,
    within_range: undefined,
    is_higher_better: undefined,
    pairing_logic: EPairType.FIRST_FIRST,
    // complete_timeout: undefined,
    // complete_timeout_unit: EWithinUnitType.SECOND,
  },
};

const defaultSingleEventMetricData = {
  ...metricBaseInfo,
  metrics_type: EMetricTypes.SINGLE_EVENT,
  definition: {
    event: undefined,
    calculation: 'count',
    attribute: undefined,
    attribute_type: undefined,
    unit: undefined,
    is_higher_better: undefined,
  },
};
const defaultRatioMetricData = {
  ...metricBaseInfo,
  metrics_type: EMetricTypes.RATIO_METRIC,
  definition: {
    numerator: {
      used_metric_id: undefined,
      event: undefined,
      calculation: 'count',
      attribute: undefined,
      attribute_type: undefined,
    },
    denominator: {
      used_metric_id: undefined,
      is_numerator_filter_enabled: false,
      event: undefined,
      calculation: 'count',
      attribute: undefined,
      attribute_type: undefined,
      fixed_number: undefined,
    },
    unit: undefined,
    is_higher_better: true,
  },
};

const defaultDataMap = {
  [EMetricTypes.DURATION]: defaultDurationMetricData,
  [EMetricTypes.CONVERSION]: defaultConversionMetricData,
  [EMetricTypes.SINGLE_EVENT]: defaultSingleEventMetricData,
  [EMetricTypes.RATIO_METRIC]: defaultRatioMetricData,
};

export const useBuildMetricFormData = () => {
  /**
   * Builds metric form data based on metric type.
   * This function can be called in four ways:
   * 1. No arguments: Returns default conversion metric data
   * 2. Single argument (data): Uses the metric type from the data object or defaults to conversion
   * 3. Single argument (type): Returns default data for the specified metric type
   * 4. Two arguments (type, data): Uses the specified metric type and merges with provided data
   *
   * @overload
   * @returns {any} Default conversion metric data
   *
   * @overload
   * @param {any} data - The metric data object containing metrics_type or defaults to conversion
   * @returns {any} Merged metric data with defaults
   *
   * @overload
   * @param {EMetricTypes} type - The type of metric (DURATION, CONVERSION, or SINGLE_EVENT)
   * @returns {any} Default data for the specified metric type
   *
   * @overload
   * @param {EMetricTypes} type - The type of metric (DURATION, CONVERSION, or SINGLE_EVENT)
   * @param {any} data - The metric data to merge with defaults
   * @returns {any} A deep clone of the merged metric data with default values
   */
  function buildMetricFormData(type: EMetricTypes): any;
  function buildMetricFormData(data?: any): any;
  function buildMetricFormData(type: EMetricTypes, data: any): any;
  function buildMetricFormData(arg1?, arg2?) {
    let type;
    let data;
    if (arguments.length === 0) {
      type = EMetricTypes.CONVERSION;
      data = defaultDataMap[type];
    }
    if (arguments.length === 1) {
      type = arg1?.metrics_type ?? EMetricTypes.CONVERSION;
      data = arg1;
    }
    if (arguments.length === 2) {
      type = arg1;
      data = arg2;
    }
    const defaultMetricData = defaultDataMap[type];
    const cleaned = mergeWithDefaults(data, defaultMetricData);
    return _.cloneDeep(cleaned);
  }

  const buildFunction = useCallback(buildMetricFormData, []);

  return { buildMetricFormData: buildFunction };
};
